import {
  Component,
  Input,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  inject,
  OnInit,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { DsTooltipDirective } from 'src/app/shared/components/ds-tooltip/ds-tooltip.component';
import { TranslateModule } from '@ngx-translate/core';
import { combineLatest } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationItem } from 'src/app/shared/components/navigation/navigation.model';
import { UserResourceDirective } from 'src/app/shared/directives/has-resource/user-resource.directive';
import { BusinessUnitService } from 'src/app/modules/master-data/business-unit/business-unit.service';
import { TenantService } from 'src/app/modules/administration/tenant/tenant.service';
import { ContextSelectorService } from 'src/app/core/services/context-selector.service';
import { IBusinessUnit } from 'src/app/modules/master-data/business-unit/business-unit';
import { ITenant } from 'src/app/modules/administration/tenant/tenant';
import { pathRequiresTenant } from 'src/app/core/auth/guards/tenant-exempt-paths';

/** Tên symbol trong sprite `assets/icons/rail.svg`, mang tiền tố namespace để cổng sprite đọc được. */
type RailIconName = `rail:${string}`;

/**
 * Icon rail theo thiết kế (module-rail.jsx, bảng I): nét mảnh 1.7, khác bộ Material của phần
 * còn lại của app — Alex chốt 31/10 riêng rail theo thiết kế. Khoá là mã module của rail
 * (navigation.ts). Chuỗi viết ĐẦY ĐỦ tiền tố để `icons:check:sprites` thấy từng tên và đỏ khi
 * sprite thiếu symbol. Mã chưa có trong bảng rơi về icon font cũ — không bao giờ ô trống.
 */
export const RAIL_ICONS: Readonly<Record<string, RailIconName>> = {
  POS: 'rail:pos',
  SM: 'rail:sm',
  CRM: 'rail:crm',
  WM: 'rail:wm',
  LM: 'rail:lm',
  PROC: 'rail:proc',
  PLAN: 'rail:plan',
  MFG: 'rail:mfg',
  FIN: 'rail:fin',
  PRICING: 'rail:pricing',
  AM: 'rail:am',
  HRM: 'rail:hrm',
  PM: 'rail:pm',
  OKR: 'rail:okr',
  MD: 'rail:md',
  SEC: 'rail:sec',
  AC: 'rail:ac',
  GOV: 'rail:gov',
  RISK: 'rail:risk',
  FUSION: 'rail:fusion',
  PLAT: 'rail:plat',
  PROFILE: 'rail:profile',
  KB: 'rail:kb',
  'COMP-LIB': 'rail:comp-lib',
};

const RAIL_SPRITE = 'assets/icons/rail.svg';
const RAIL_CHEVRON: RailIconName = 'rail:chevron';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'module-rail',
  imports: [RouterModule, DsTooltipDirective, TranslateModule, UserResourceDirective],
  templateUrl: './module-rail.component.html',
  styleUrls: ['./module-rail.component.scss'],
})
export class ModuleRailComponent implements OnInit {
  private _tenantService = inject(TenantService);
  private _businessUnitService = inject(BusinessUnitService);
  private _contextSelectorService = inject(ContextSelectorService);
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);

  @Input() navigation: NavigationItem[] = [];

  selectedTenant: ITenant | null | undefined = null;
  selectedBusinessUnit: IBusinessUnit | null | undefined = null;
  /** 2–3 chữ cái đầu của đơn vị (hoặc tenant) hiện trong ô bối cảnh 32×32. */
  ctxInitials = '';
  /** Tên đầy đủ tenant · đơn vị cho tooltip + aria-label của ô bối cảnh. */
  ctxTooltip = '';
  /**
   * Panel bối cảnh đang mở. Panel nằm NGAY bên phải rail (Q-061), đúng chỗ tooltip của ô bối
   * cảnh hiện ra — nên tắt tooltip đó khi panel mở, kẻo nó đè lên tiêu đề panel.
   */
  readonly ctxPanelOpen = this._contextSelectorService.isOpenSignal;
  /** True once the account has ≥1 tenant. Drives dimming of business rail items. */
  hasTenant = false;

  ngOnInit(): void {
    combineLatest([
      this._tenantService.selectedTenant$,
      this._businessUnitService.selectedBusinessUnit$,
      this._tenantService.tenants$,
    ])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([tenant, bu, tenants]) => {
        this.selectedTenant = tenant;
        this.selectedBusinessUnit = bu;
        this.ctxInitials = ModuleRailComponent.initials(
          bu?.displayName || bu?.name || tenant?.displayName || tenant?.name || '',
        );
        this.ctxTooltip = [tenant?.displayName || tenant?.name, bu?.displayName || bu?.name]
          .filter(Boolean)
          .join(' · ');
        this.hasTenant = (tenants?.length ?? 0) > 0;
        this._cdr.markForCheck();
      });
  }

  /**
   * A rail item is unreachable when it already carries `disabled`, OR when the account has
   * no tenant and the item points at a business module (tenantGuard would redirect it anyway).
   * `tenants$` is the same source tenantGuard reads, so rail + guard can never disagree.
   */
  isItemDisabled(item: NavigationItem): boolean {
    if (item.disabled) return true;
    // External links (docs, tools) aren't tenant-scoped app routes — never dim them.
    if (item.externalLink) return false;
    return !this.hasTenant && !!item.link && pathRequiresTenant(item.link);
  }

  /** All navigation groups — HUB flows in the scroll area like every other group
   *  (bottom pin removed 2026-08-04 per Alex). */
  get groups(): NavigationItem[] {
    return this.navigation;
  }

  /** Đường dẫn symbol trong sprite rail cho thẻ use, hoặc null để rơi về icon font. */
  railIconHref(item: NavigationItem): string | null {
    const name = item.id ? RAIL_ICONS[item.id] : undefined;
    return name ? ModuleRailComponent.spriteHref(name) : null;
  }

  readonly chevronHref = ModuleRailComponent.spriteHref(RAIL_CHEVRON);

  static spriteHref(name: RailIconName): string {
    return `${RAIL_SPRITE}#${name.slice('rail:'.length)}`;
  }

  /** Toggle the context selector panel */
  onCoreClick(): void {
    this._contextSelectorService.toggle();
  }

  /**
   * Chữ cái đầu (tối đa 3) của các TỪ bắt đầu bằng chữ hoa — 'Carot Food HCM' → 'CFH'.
   * Không có từ viết hoa nào thì lấy 2 ký tự đầu. Static để spec gọi không cần TestBed.
   * Thay `_computeBuShortCode` cũ ('UAT HE', 6 ký tự): ô bối cảnh v2 chỉ rộng 32px.
   */
  static initials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return '';
    const caps = words.filter((w) => /^\p{Lu}/u.test(w)).map((w) => w[0]);
    return (caps.length > 0 ? caps.slice(0, 3).join('') : name.trim().slice(0, 2)).toUpperCase();
  }
}
